import * as React from 'react';
import styled from 'styled-components';

import { BREAKPOINTS, COLORS } from '../styles';
import { Tip, Attention, ThumbsUp, Help, Close } from '../icons';
import { Button } from '../Button';

/**
 * Styles for <Banner />
 */
const BannerContainer = styled.div<BannerProps>`
  background: ${(props: BannerProps) => (props.type ? props.type : COLORS.correct_100)};
  min-height: 48px;
  display: flex;
  color: ${COLORS.black};
  margin-top: ${(props: BannerProps) => (props.bottom ? 'auto' : '0')};

  button:last-child {
    margin: 0 0 0 auto;
    line-height: 0;
    cursor: pointer;
  }
  button:last-child:hover {
    margin: 0 0 0 auto;
    background: ${(props: BannerProps) => (props.hover ? props.hover : COLORS.primary_20)};
  }

  a {
    color: ${(props: BannerProps) => (props.link)};
    font-weight: bold;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }

  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 140%;
`;

const BannerCenter = styled.div`
  color: ${(props) => props.color};
  font-size: 18px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin: 0 8px;
  ${BREAKPOINTS.MEDIUM} {
    margin: 0 32px;
  }
  ${BREAKPOINTS.LARGE} {
    margin: 0 56px;
  }
  ${BREAKPOINTS.XLARGE} {
    width: 1120px;
    margin: 0 auto;
  }
  >svg {
    margin-right:8px;
  }
`;

type BannerProps = {
  type?: string;
  link?: string;
  linkText?: string;
  hover?: string;
  bottom?: boolean;
  testId?: string;
  onClose?: () => void;
};

const Banner: React.FunctionComponent<BannerProps> = ({ type, children, bottom, testId, linkText, link, onClose }) => {
  const formatTypeToLowerCase = type?.toLowerCase();
  switch (formatTypeToLowerCase) {
    case 'warning':
      return (
        <BannerContainer data-testid={testId} type={COLORS.warning_100} link={COLORS.warning_700} hover={COLORS.warning_20} bottom={bottom}>
          <BannerCenter color={COLORS.warning_700}>
            <Help color={COLORS.warning_700} size="24px" />
            {children}&nbsp;&nbsp;
            {link && linkText && <a href={link} target="_blank">{linkText}</a>}
            <Button variant="text" onClick={() => onClose && onClose()}>
              <Close color={COLORS.warning_500} size="24px" />
            </Button>
          </BannerCenter>
        </BannerContainer>
      );
    case 'critical':
      return (
        <BannerContainer data-testid={testId} type={COLORS.critical_100} link={COLORS.critical_700} hover={COLORS.critical_20} bottom={bottom}>
          <BannerCenter color={COLORS.critical_700}>
            <Attention color={COLORS.critical_700} size="24px" />
            {children}&nbsp;&nbsp;
            {link && linkText && <a href={link} target="_blank">{linkText}</a>}
            <Button variant="text" onClick={() => onClose && onClose()}>
              <Close color={COLORS.critical_500} size="24px" />
            </Button>
          </BannerCenter>
        </BannerContainer>
      );
    case 'positive':
      return (
        <BannerContainer data-testid={testId} type={COLORS.correct_100} link={COLORS.correct_700} hover={COLORS.correct_20} bottom={bottom}>
          <BannerCenter color={COLORS.correct_700}>
            <ThumbsUp color={COLORS.correct_700} size="24px" />
            {children}&nbsp;&nbsp;
            {link && linkText && <a href={link} target="_blank">{linkText}</a>}
            <Button variant="text" onClick={() => onClose && onClose()}>
              <Close color={COLORS.correct_500} size="24px" />
            </Button>
          </BannerCenter>
        </BannerContainer>
      );
    default:
      return (
        <BannerContainer data-testid={testId} type={COLORS.primary_100} link={COLORS.primary_700} hover={COLORS.primary_20} bottom={bottom}>
          <BannerCenter color={COLORS.primary_700}>
            <Tip color={COLORS.primary_700} size="24px" />
            {children}&nbsp;&nbsp;
            {link && linkText && <a href={link} target="_blank">{linkText}</a>}
            <Button variant="text" onClick={() => onClose && onClose()}>
              <Close color={COLORS.primary_500} size="24px" />
            </Button>
          </BannerCenter>
        </BannerContainer>
      );
  }
};

export default Banner;
